/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

export const FEE_PROPERTIES = {
    deposit_fee: {
        default: '0',
        attribute: 'Deposit',
    },
    electricity_fee: {
        default: '0',
        attribute: 'Electricity Hookup',
    },
    holiday_fee: {
        default: '0',
        attribute: 'Holiday',
    },
    per_person_fee: {
        default: '0',
        attribute: 'Per Person',
    },
    premium_fee: {
        default: '0',
        attribute: 'Premium',
    },
    sewer_hookup_fee: {
        default: '0',
        attribute: 'Sewer Hookup',
    },
    use_fee: {
        default: '0',
        attribute: 'Use',
    },
    water_hookup_fee: {
        default: '0',
        attribute: 'Water Hookup',
    },
    weekend_fee: {
        default: '0',
        attribute: 'Weekend',
    },
    partial_payment_deposit: {
        default: '0',
        attribute: 'Partial Payment Deposit',
    },
    due_now: {
        default: '0',
        attribute: 'Long Term Stay - Due Now',
    },
    non_resident_fee: {
        default: '0',
        attribute: 'Out of State Surcharge',
    },
};

export const FEE_TEMPLATES_DATA_STRUCTURE = (() => {
    const fees = [
        {
            index: 1,
            fee_name: 'Use Fee',
            amount: '0.00',
            fee_type: 'use_fee',
            description: 'Use Fee is the base rent charge for the campsite',
        },
        {
            index: 2,
            fee_name: 'Premium Fee',
            amount: '0.00',
            fee_type: 'premium_fee',
            description: 'Premium Fee is extra fees on top of regular Use Fee',
        },
        {
            index: 3,
            fee_name: 'Weekend Fee',
            amount: '0.00',
            fee_type: 'weekend_fee',
            description: 'Weekend Fee is extra fees charged during weekends',
        },
        {
            index: 4,
            fee_name: 'Holiday Fee',
            amount: '0.00',
            fee_type: 'holiday_fee',
            description:
                'Holiday (Weekend) Fee is extra fees charged during holidays that fall on weekends',
        },
        {
            index: 5,
            fee_name: 'Electricity Fee',
            amount: '0.00',
            fee_type: 'electricity_fee',
            description:
                'Electricity Fee is extra attribute fees charge for campsites that offer electricity',
        },
        {
            index: 6,
            fee_name: 'Water Hookup Fee',
            amount: '0.00',
            fee_type: 'water_hookup_fee',
            description:
                'Water Hookup Fee is extra attribute fees charge for campsites that offer water hookups',
        },
        {
            index: 7,
            fee_name: 'Sewer Hookup Fee',
            amount: '0.00',
            fee_type: 'sewer_hookup_fee',
            description:
                'Sewage Hookup Fee is extra attribute fees charge for campsites that offer sewage hookups',
        },
        {
            index: 8,
            fee_name: 'Per Person Fee',
            amount: '0.00',
            fee_type: 'per_person_fee',
            description:
                'Per Person Fee is fees charged according to the total number of occupants for the campsite',
        },
        {
            index: 9,
            fee_name: 'Security Deposit Fee',
            amount: '0.00',
            fee_type: 'deposit_fee',
            description:
                'Security Deposit Fee is the refundable security deposits collected for the campsite',
        },
        {
            index: 10,
            fee_name: 'Down Payment',
            amount: '0.00',
            fee_type: 'partial_payment_deposit',
            description:
                'A Down Payment is an additional, non refundable fee due at the time of booking. All other use fees will be required to be paid by the customer 30 days prior to arrival',
        },
        {
            index: 11,
            fee_name: 'Length of Stay Discount - Percentage',
            fee_type: 'length_of_stay_discount',
            description:
                'A length of stay discount provides a specific percentage discount to customers based on the amount of nights a customer stays for.',
        },
        {
            index: 12,
            fee_name: 'Length of Stay Discount - Flat Fee',
            fee_type: 'length_of_stay_rate_cut',
            description:
                'A length of stay discount provides a specific rate discount to customers based on the amount of nights a customer stays for.',
        },
        {
            index: 13,
            fee_name: 'Long Term Stay - Due Now',
            amount: '0.00',
            fee_type: 'due_now',
            description:
                'Long Term Stay - Due Now is the nonrefundable payment due at booking required to secure reservation dates for a long term stay.',
        },
    ];

    if (process.env.CAMPING_OPTIONS?.enableNonResidentFee) {
        fees.push({
            index: fees.length + 1,
            fee_name: 'Out of State Surcharge',
            fee_type: 'non_resident_fee',
            amount: '0.00',
            description:
                'This is a unique line item applied per night during the stay for visitors who do not live in your state. Out of state is determined by the reservation holder’s mailing address on their user account. Only reservation holders with accounts that list your state within their mailing address will not get charged this line item. For example, skipping account creation in field sales will charge this fee on a reservation (Primary Occupant is not the account).',
        });
    }

    fees.push({
        index: fees.length + 1,
        fee_name: 'Total',
        amount: '0.00',
        fee_type: null,
    });

    return fees;
})();

// Array of fee line items that can be modified by internal mng
export const ADJUSTABLE_FEES_LINE_ITEMS = [
    { index: 1, fee_type: 2, fee_name: 'Use Fees', fee_id: 'use_fee', amount: '0.00' },
    {
        index: 3,
        fee_type: 4,
        fee_name: 'Electricity Hookup',
        fee_id: 'electricity_hookup',
        amount: '0.00',
    },
    {
        index: 4,
        fee_type: 4,
        fee_name: 'Water Hookup',
        fee_id: 'water_hookup',
        amount: '0.00',
    },
    {
        index: 5,
        fee_type: 4,
        fee_name: 'Sewer Hookup',
        fee_id: 'sewer_hookup',
        amount: '0.00',
    },
    { index: 6, fee_type: 8, fee_name: 'Deposit', fee_id: 'deposit', amount: '0.00' },
];

export const DAY_OF_WEEK_FEES_LINE_ITEMS = [
    { index: 1, fee_type: 'use_fee', fee_name: 'Monday', fee_id: 'use_fee', amount: 0 },
    { index: 2, fee_type: 'use_fee', fee_name: 'Tuesday', fee_id: 'use_fee', amount: 0 },
    {
        index: 3,
        fee_type: 'use_fee',
        fee_name: 'Wednesday',
        fee_id: 'use_fee',
        amount: 0,
    },
    { index: 4, fee_type: 'use_fee', fee_name: 'Thursday', fee_id: 'use_fee', amount: 0 },
    { index: 5, fee_type: 'use_fee', fee_name: 'Friday', fee_id: 'use_fee', amount: 0 },
    { index: 6, fee_type: 'use_fee', fee_name: 'Saturday', fee_id: 'use_fee', amount: 0 },
    { index: 7, fee_type: 'use_fee', fee_name: 'Sunday', fee_id: 'use_fee', amount: 0 },
];

export const PATH_INVOLVING_FEE_CHANGE = [
    'site_occupant.num_occupants',
    'site_occupant.pass',
    'campsite_id',
    'start_date',
    'end_date',
    'reservation_options',
    'waive_fee_options',
    'site_occupant.custom_order_details.group_size.group_size',
    'night_map',
];
